import { Component, Input, OnInit } from '@angular/core';
import { BehaviorSubject, filter, takeUntil } from 'rxjs';
import { ChartConfiguration, ChartType } from 'chart.js';
import { CollectionSummaryState, CustodianCollectionData } from '@models/ui/case-analytics-state/collection-summary-state.model';
import { appear } from '../../animations/appear';
import { CaseAnalyticsItemBaseComponent } from '../case-analytics-item-base.component';

@Component({
    selector: 'lex-case-analytics-collection-summary',
    templateUrl: './case-analytics-collection-summary.component.html',
    styleUrl: './case-analytics-collection-summary.component.scss',
    animations: [
        appear
    ],
    standalone: false
})
export class CaseAnalyticsCollectionSummaryComponent extends CaseAnalyticsItemBaseComponent implements OnInit {
  @Input() set collectionSummaryState(state: CollectionSummaryState) {
    this.collectionSummaryState$.next(state);
  }

  public shouldShowLoader: boolean = true;
  public periodSummary: PeriodSummary = null;
  public collectionChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
        },
        onClick: null,
      },
      tooltip: null,
    }
  };
  public collectionChartData = {
    datasets: [],
    labels: []
  };
  public collectionChartType: ChartType = 'line';

  private collectionSummaryState$ = new BehaviorSubject<CollectionSummaryState>(null);

  ngOnInit(): void {
    this.collectionSummaryState$.pipe(
        filter((response) => (!!response)),
        takeUntil(this.destroy$)
      ).subscribe((data) => {
      if (data.summaryData.length < 1) {
        this.noDataInCase$.next(true);
        return;
      }

      const sortedData = data.summaryData.sort((a, b) => {
        if (a.period < b.period) return -1;
        if (a.period > b.period) return 1;
        return 0;
      });

      const sortedCustodians = data.custodians.sort();
      const labels = sortedData.map(d => d.period);
      const custodianMap: Map<string, CustodianCollectionData[]> = new Map();
      sortedCustodians.forEach(c => custodianMap.set(c, [{
        name: c,
        count: null
      }]));
      sortedData.forEach((periodData, index) => {
        sortedCustodians.forEach(c =>
          custodianMap.get(c)[index] = {
            name: c,
            count: null
          }
        );
        periodData.custodianData.forEach((custodian) => {
          const custodianData = custodianMap.get(custodian.name);
          custodianData[index] = {
            name: custodian.name,
            count: custodian.count,
          };
        });

      });
      const datasets = Array.from(custodianMap, ([key, value]) => ({
        label: key,
        data: value.map(item => item.count),
        fill: false,      
        tension: 0.1
      }));
  
      this.collectionChartData = {
        labels: labels,
        datasets: datasets
      };
      this.shouldShowLoader = false;
    });
  }

  public displayPeriodSummary(chartEvent) {
    if (chartEvent.active.length === 1) {
      const i = chartEvent.active[0].index;
      const masterDateDocCount = this.collectionChartData.datasets[0].data[i];
      const masterDate = this.collectionChartData.labels[i];
      this.periodSummary = {
        masterDate: masterDate,
        docCount: masterDateDocCount,
      };
    }
    else {
      this.periodSummary = null;
    }
  }
}

interface PeriodSummary {
  masterDate: string,
  docCount: any
}
